import React, { useState, useEffect } from 'react';
import { TextField, Button, Container, Box, Typography } from '@mui/material';
import { createClient, getClientById, updateClient } from '../services/clientService';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import MediaUploadField from './MediaUploadField';

function ClientForm() {
  const [initialValues, setInitialValues] = useState({
    clientId: null,
    name: '',
    nameAr: '',
    title: '',
    titleAr: '',
    description: '',
    descriptionAr: '',
    story: '',
    storyAr: '',
    logoUrl: '',
    slug: '',
    metaDescription: '',
    metaDescriptionAr: '',
    targetKeyword: '',
  });
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      fetchClient();
    }
  }, [id]);

  const fetchClient = async () => {
    try {
      const data = await getClientById(id);
      setInitialValues({
        clientId: data.clientId,
        name: data.name,
        nameAr: data.nameAr || '',
        title: data.title,
        titleAr: data.titleAr || '',
        description: data.description,
        descriptionAr: data.descriptionAr || '',
        story: data.story,
        storyAr: data.storyAr || '',
        logoUrl: data.logoUrl,
        slug: data.slug || generateSlug(data.name),
        metaDescription: data.metaDescription,
        metaDescriptionAr: data.metaDescriptionAr || '',
        targetKeyword: data.targetKeyword,
      });
    } catch (error) {
      console.error('Error fetching client:', error);
    }
  };

  const ClientSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    title: Yup.string().required('Title is required'),
    description: Yup.string(),
    story: Yup.string(),
    logoUrl: Yup.string(),
    slug: Yup.string().required('Slug is required'),
    metaDescription: Yup.string(),
    targetKeyword: Yup.string(),
    // Arabic fields are optional
    nameAr: Yup.string().max(100, 'Arabic name should not exceed 100 characters'),
    titleAr: Yup.string().max(100, 'Arabic title should not exceed 100 characters'),
  });

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '');
  };

  const handleSubmit = async (values) => {
    try {
      if (id) {
        await updateClient(id, values);
      } else {
        await createClient(values);
      }
      navigate('/clients');
    } catch (error) {
      console.error('Error saving client:', error);
    }
  };

  return (
    <DashboardLayout>
      <Container maxWidth="md">
        <Box mt={4}>
          <Typography variant="h4" gutterBottom>
            {id ? 'Edit Client' : 'Create Client'}
          </Typography>
          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={ClientSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, setFieldValue }) => (
              <Form>
                {/* English Fields */}
                <Typography variant="h6" sx={{ mt: 3, mb: 2, color: 'primary.main' }}>
                  English Content
                </Typography>

                <Box display="flex" gap={2}>
                  <TextField
                    label="Name"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    required
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                  />
                  <TextField
                    label="Arabic Name"
                    name="nameAr"
                    value={values.nameAr}
                    onChange={handleChange}
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    error={touched.nameAr && Boolean(errors.nameAr)}
                    helperText={touched.nameAr && errors.nameAr}
                    inputProps={{ dir: 'rtl' }}
                  />
                </Box>

                <TextField
                  label="Slug"
                  name="slug"
                  value={values.slug}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  required
                  error={touched.slug && Boolean(errors.slug)}
                  helperText={touched.slug && errors.slug}
                />

                <Box display="flex" gap={2}>
                  <TextField
                    label="Title"
                    name="title"
                    value={values.title}
                    onChange={handleChange}
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    required
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title && errors.title}
                  />
                  <TextField
                    label="Arabic Title"
                    name="titleAr"
                    value={values.titleAr}
                    onChange={handleChange}
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    error={touched.titleAr && Boolean(errors.titleAr)}
                    helperText={touched.titleAr && errors.titleAr}
                    inputProps={{ dir: 'rtl' }}
                  />
                </Box>

                <Box display="flex" gap={2}>
                  <TextField
                    label="Description"
                    name="description"
                    value={values.description}
                    onChange={handleChange}
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    multiline
                    rows={3}
                  />
                  <TextField
                    label="Arabic Description"
                    name="descriptionAr"
                    value={values.descriptionAr}
                    onChange={handleChange}
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    multiline
                    rows={3}
                    inputProps={{ dir: 'rtl' }}
                  />
                </Box>

                {/* Story Fields */}
                <Typography variant="h6" sx={{ mt: 3, mb: 2, color: 'primary.main' }}>
                  Client Story
                </Typography>

                <Box mt={2} mb={2}>
                  <Typography variant="subtitle1" gutterBottom>
                    Story (English)
                  </Typography>
                  <ReactQuill
                    theme="snow"
                    value={values.story}
                    onChange={(value) => setFieldValue('story', value)}
                    modules={{
                      toolbar: [
                        [{ header: '1' }, { header: '2' }, { font: [] }],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                        [{ align: [] }],
                        [{ color: [] }, { background: [] }],
                        ['link', 'image'],
                        ['clean'],
                      ],
                    }}
                    formats={[
                      'header',
                      'font',
                      'list',
                      'bullet',
                      'bold',
                      'italic',
                      'underline',
                      'strike',
                      'blockquote',
                      'align',
                      'color',
                      'background',
                      'link',
                      'image',
                    ]}
                  />
                  {touched.story && errors.story && (
                    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                      {errors.story}
                    </Typography>
                  )}
                </Box>

                <Box mt={2} mb={2}>
                  <Typography variant="subtitle1" gutterBottom>
                    Story (Arabic)
                  </Typography>
                  <ReactQuill
                    theme="snow"
                    value={values.storyAr}
                    onChange={(value) => setFieldValue('storyAr', value)}
                    modules={{
                      toolbar: [
                        [{ header: '1' }, { header: '2' }, { font: [] }],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                        [{ align: [] }],
                        [{ color: [] }, { background: [] }],
                        ['link', 'image'],
                        ['clean'],
                      ],
                    }}
                    formats={[
                      'header',
                      'font',
                      'list',
                      'bullet',
                      'bold',
                      'italic',
                      'underline',
                      'strike',
                      'blockquote',
                      'align',
                      'color',
                      'background',
                      'link',
                      'image',
                    ]}
                  />
                </Box>

                {/* SEO and Media Fields */}
                <Typography variant="h6" sx={{ mt: 3, mb: 2, color: 'primary.main' }}>
                  SEO & Media
                </Typography>

                <MediaUploadField
                  label="Client Logo"
                  value={values.logoUrl}
                  onChange={(nextValue) => setFieldValue('logoUrl', nextValue)}
                  kind="image"
                  scope="clients"
                  slug={values.slug || values.name}
                  error={errors.logoUrl}
                  touched={touched.logoUrl}
                  helperText="You can still paste an external/manual URL if needed."
                />

                <Box display="flex" gap={2}>
                  <TextField
                    label="Meta Description"
                    name="metaDescription"
                    value={values.metaDescription}
                    onChange={handleChange}
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    multiline
                    rows={2}
                  />
                  <TextField
                    label="Arabic Meta Description"
                    name="metaDescriptionAr"
                    value={values.metaDescriptionAr}
                    onChange={handleChange}
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    multiline
                    rows={2}
                    inputProps={{ dir: 'rtl' }}
                  />
                </Box>

                <TextField
                  label="Target Keyword"
                  name="targetKeyword"
                  value={values.targetKeyword}
                  onChange={handleChange}
                  variant="outlined"
                  margin="normal"
                  fullWidth
                />

                <Box mt={4}>
                  <Button type="submit" variant="contained" color="primary" fullWidth>
                    {id ? 'Update Client' : 'Create Client'}
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Container>
    </DashboardLayout>
  );
}

export default ClientForm;
